// Infinite-canvas gestures. The viewport belongs to the session, not to an
// individual image, so a new render, favorite, or stream tile drops into the
// exact position and scale the visitor was already exploring.
//
// One finger (or a mouse drag) pans. Two fingers pinch-zoom, anchored under
// the fingers, and their midpoint drift pans at the same time — the slider
// still exists, but on a phone it is half-hidden by the thumb using it, and
// pinching the pattern is what hands already try first. The stage's
// `touch-action: none` is what keeps the browser's own page zoom from
// swallowing the gesture.

import { panBackgroundBy } from './background.js';
import { pinchZoomBy } from './tilesize.js';

const stage = document.getElementById('stage');
const DRAG_THRESHOLD = 3;
// Below this spread, the ratio between two touch points is mostly sensor
// noise — two fingers landing 5px apart that wobble to 10px would read as a
// 2x zoom lurch. Real pinches start far wider than this.
const PINCH_MIN_SPREAD = 24;

const pointers = new Map(); // pointerId -> last seen {x, y}
let distance = 0;
let suppressClick = false;
let pinchDist = 0;
let pinchMid = null;

stage.addEventListener('pointerdown', (event) => {
  if (event.button !== 0) return;
  if (pointers.size >= 2) return; // two fingers own the gesture; ignore more
  pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
  stage.setPointerCapture(event.pointerId);
  if (pointers.size === 1) {
    distance = 0;
    suppressClick = false;
  } else {
    // A second finger turns the gesture into a pinch, never a click.
    const [a, b] = [...pointers.values()];
    pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
    pinchMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    suppressClick = true;
    document.body.classList.add('canvas-dragging');
  }
});

stage.addEventListener('pointermove', (event) => {
  const p = pointers.get(event.pointerId);
  if (!p) return;

  if (pointers.size === 2) {
    p.x = event.clientX;
    p.y = event.clientY;
    const [a, b] = [...pointers.values()];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    if (pinchDist > PINCH_MIN_SPREAD && dist > PINCH_MIN_SPREAD) {
      pinchZoomBy(dist / pinchDist, mid.x, mid.y);
    }
    if (pinchMid) panBackgroundBy(mid.x - pinchMid.x, mid.y - pinchMid.y);
    pinchDist = dist;
    pinchMid = mid;
    return;
  }

  const dx = event.clientX - p.x;
  const dy = event.clientY - p.y;
  p.x = event.clientX;
  p.y = event.clientY;
  distance += Math.hypot(dx, dy);
  if (distance < DRAG_THRESHOLD) return;
  panBackgroundBy(dx, dy);
  suppressClick = true;
  document.body.classList.add('canvas-dragging');
});

function finishDrag(event) {
  if (!pointers.delete(event.pointerId)) return;
  if (pointers.size === 1) {
    // The pinch relaxes into a one-finger pan; the survivor's coordinates
    // are already current, so nothing jumps, and the threshold is spent —
    // this is a continuation, not a fresh gesture.
    pinchDist = 0;
    pinchMid = null;
    distance = DRAG_THRESHOLD;
  } else if (pointers.size === 0) {
    pinchDist = 0;
    pinchMid = null;
    document.body.classList.remove('canvas-dragging');
  }
}

stage.addEventListener('pointerup', finishDrag);
stage.addEventListener('pointercancel', finishDrag);

/** A drag ends with a synthetic click; consume it so panning the normal canvas
 *  does not also open fullscreen preview. */
export function consumeCanvasDragClick() {
  const consumed = suppressClick;
  suppressClick = false;
  return consumed;
}
